import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { usePrompt } from '@/contexts/PromptContext'
import { useAutoResizeTextarea } from '@/hooks/useAutoResizeTextarea'

// Extend window interface for timeout and pending save
declare global {
  interface Window {
    actionUpdateTimeout?: NodeJS.Timeout
    actionPendingSave?: () => Promise<void>
  }
}

interface ActionStepProps {
  value?: string
  onChange?: (value: string) => void
  readOnly?: boolean
}

const ActionStep: React.FC<ActionStepProps> = ({
  value: externalValue,
  onChange: externalOnChange,
  readOnly = false
}) => {
  const { t } = useTranslation('steps')
  const [internalAction, setInternalAction] = useState('')
  const { promptData, updatePrompt, loading } = usePrompt()
  const lastSavedActionRef = useRef<string>('')

  // Use external value if provided (for refine mode), otherwise use internal state
  const action = externalValue !== undefined ? externalValue : internalAction
  const setAction = externalOnChange || setInternalAction
  const placeholder = t('action.placeholder')
  const textareaRef = useAutoResizeTextarea(action, placeholder)

  // Load existing action data when promptData is available
  // Only update if:
  // 1. Initial load (internalAction is empty), OR
  // 2. The local state matches what we last saved (user isn't actively typing)
  useEffect(() => {
    if (promptData?.action && !externalValue) {
      // Only update if this is initial load or if local state matches last saved value
      // This prevents real-time updates from overwriting user input while typing
      if (internalAction === '' || internalAction === lastSavedActionRef.current) {
        setInternalAction(promptData.action)
        lastSavedActionRef.current = promptData.action
      }
    }
  }, [promptData?.action, externalValue, internalAction])

  // Action is now generated in the background when the prompt is created in Home.tsx
  // This component just displays and manages the action

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (window.actionUpdateTimeout) {
        clearTimeout(window.actionUpdateTimeout)
      }
    }
  }, [])

  // Handle value changes with debounced database updates
  const handleActionChange = (newAction: string) => {
    setAction(newAction)
    
    // If we're in refine mode (external onChange), don't update database
    if (externalOnChange) return
    
    // Clear any existing timeout
    if (window.actionUpdateTimeout) {
      clearTimeout(window.actionUpdateTimeout)
    }
    
    // Create the save function
    const saveFunction = async () => {
      try {
        await updatePrompt({ action: newAction })
        // Update the ref to track what we just saved
        lastSavedActionRef.current = newAction
        // Clear the pending save after it completes
        delete window.actionPendingSave
      } catch (error) {
        console.error('Error updating action:', error)
        delete window.actionPendingSave
      }
    }
    
    // Store the save function so it can be executed immediately if needed
    window.actionPendingSave = saveFunction
    
    // Debounce database updates to avoid interfering with typing
    window.actionUpdateTimeout = setTimeout(saveFunction, 1000) // 1 second debounce
  }

  return (
    <div className="text-center">
      <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4 mt-2 sm:mt-6 px-4">
        {t('action.title')}
      </h2>

      <p className="text-gray-600 mb-6 sm:mb-8 max-w-lg mx-auto px-4 text-xs sm:text-base">
        {t('action.description')}
      </p>

      {action.trim() ? (
        <div className="max-w-2xl mx-auto mb-6 sm:mb-8 px-4">
          <textarea
            ref={textareaRef}
            placeholder={t('action.placeholder')}
            value={action}
            onChange={(e) => handleActionChange(e.target.value)}
            disabled={readOnly || loading}
            className="w-full min-h-12 p-3 text-sm sm:text-lg border border-gray-300 rounded-lg resize-none overflow-y-auto focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
            rows={1}
          />
        </div>
      ) : (
        <div className="text-center text-xs sm:text-sm text-gray-500 mb-8">
          {t('action.loading')}
        </div>
      )}
    </div>
  )
}

export default ActionStep
